import {
  type Firestore,
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { type Functions, httpsCallable } from 'firebase/functions';

import {
  ReviewError,
  type EmployeeCandidate,
  type RegistrationReviewPort,
} from '../../features/committee/application/ports/registrationReviewPort';

/**
 * Firestore and Cloud Functions implementation of `RegistrationReviewPort`
 * (blueprint §11.1).
 *
 * The lookup is a client query because the committee may already read
 * employees. The two decisions are callables because approving writes a
 * `userAccounts` link, and no client may write one at any role (ADR-004).
 */

function translate(error: unknown): ReviewError {
  const code =
    typeof error === 'object' && error !== null && 'code' in error ? String(error.code) : '';
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String(error.message)
      : '';

  // The callable refuses with a code; the message carries the reason it used.
  if (code.includes('permission-denied') || message.includes('permission')) {
    return new ReviewError('forbidden', 'لا تملك صلاحية مراجعة طلبات التسجيل.');
  }
  if (message.includes('already-linked')) {
    return new ReviewError('already_linked', 'هذا الحساب مرتبط بسجل بالفعل.');
  }
  if (message.includes('record-taken')) {
    return new ReviewError('record_taken', 'هذا السجل مرتبط بحساب آخر.');
  }
  if (message.includes('not-found')) {
    return new ReviewError('not_found', 'لم يُعثر على الطلب أو السجل.');
  }
  if (code.includes('unavailable')) {
    return new ReviewError('network', 'تعذّر الاتصال. حاول مجددًا.');
  }
  return new ReviewError('unknown', 'تعذّر إتمام العملية.');
}

export function createFirebaseRegistrationReviewAdapter(
  firestore: Firestore,
  functions: Functions,
): RegistrationReviewPort {
  const review = httpsCallable<
    { applicantUid: string; employeeId?: string; reasonAr?: string },
    { ok: boolean; decision: 'approved' | 'rejected' }
  >(functions, 'reviewRegistration');

  return {
    async findByEmployeeNumber(employeeNumber): Promise<EmployeeCandidate | null> {
      const trimmed = employeeNumber.trim();
      if (trimmed === '') return null;

      try {
        // Bounded to one: the employee number is unique, and an unbounded read
        // on a mistyped value would scan the roster (§11.5.1).
        const found = await getDocs(
          query(
            collection(firestore, 'employees'),
            where('employeeNumber', '==', trimmed),
            limit(1),
          ),
        );

        const entry = found.docs[0];
        if (!entry) return null;

        const data = entry.data();
        const readString = (key: string): string | null => {
          const value: unknown = data[key];
          return typeof value === 'string' && value !== '' ? value : null;
        };

        /*
         * Whether the record is spoken for is a second query, not a field.
         *
         * A flag on the employee could disagree with `userAccounts`, which is
         * the collection the rules actually read. Asking the source costs one
         * bounded read and cannot drift.
         */
        const claimed = await getDocs(
          query(
            collection(firestore, 'userAccounts'),
            where('employeeId', '==', entry.id),
            limit(1),
          ),
        );

        return {
          id: entry.id,
          employeeNumber: readString('employeeNumber') ?? entry.id,
          fullName: readString('fullName') ?? '',
          branchId: readString('branchId'),
          alreadyLinked: !claimed.empty,
        };
      } catch (error) {
        throw translate(error);
      }
    },

    async approve(applicantUid, employeeId): Promise<void> {
      try {
        await review({ applicantUid, employeeId });
      } catch (error) {
        throw translate(error);
      }
    },

    async reject(applicantUid, reasonAr): Promise<void> {
      try {
        // No `employeeId` is what makes this a rejection; the callable decides
        // on its presence, so it is omitted rather than sent empty.
        await review({ applicantUid, reasonAr });
      } catch (error) {
        throw translate(error);
      }
    },
  };
}
